<script setup lang="ts">
import { vNavigateLinks } from '~/entities/article/directives/v-navigate-links';
import ArticleParser from '../parser/ArticleParser.vue';

import authorAvatar from '../images/authorAvatar.svg';
import type { OutputData } from '@editorjs/editorjs';
import { resizeImageAttrs } from '~/shared/lib/resizeImage';

const props = defineProps<{
  name: string;
  description: string;
  text: OutputData;
  photo?: string | undefined;
}>();
const photoAttrs = computed(() =>
  resizeImageAttrs(props.photo || authorAvatar, {
    width: 60,
    height: 60,
    mode: 'fill',
  }),
);
</script>

<template>
  <div :class="[$style.item, $style.item_hasPhoto]">
    <div v-navigate-links :class="$style.item__text">
      <ArticleParser :blocks="props.text.blocks" />
    </div>
    <div :class="$style.item__info">
      <div :class="$style.item__info__photoWrapper">
        <img
          :src="photoAttrs.src"
          :srcset="photoAttrs.srcset"
          :alt="props.name"
          :class="$style.item__info__photo"
        />
      </div>
      <div :class="$style.item__info__inner">
        <div v-if="props.name" :class="$style.item__info__name">
          {{ props.name }}
        </div>
        <div
          v-if="props.description"
          :class="$style.item__info__description"
          v-html="props.description"
        />
      </div>

      <div :class="$style.item__info__sign" />
    </div>
  </div>
</template>

<style module lang="scss">
.item {
  margin: var(--space-3x) 0;
  border: 1px solid var(--color-gray-line);

  &__text {
    @include text_body1_medium;
    padding: var(--space-2-5x);
    border-bottom: 1px solid var(--color-gray-line);
  }

  &__info {
    display: flex;
    flex-flow: row nowrap;

    &__photo {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      object-fit: cover;
      object-position: top;

      &Wrapper {
        display: flex;
        width: 100px;
        justify-content: center;
        align-items: center;
        flex-shrink: 0;
        border-right: 1px solid var(--color-gray-line);
      }
    }

    &__inner {
      padding: var(--space-2-5x);
      flex-grow: 1;
      display: flex;
      flex-flow: column nowrap;
      gap: var(--space-0-5x);
      justify-content: center;
    }

    &__name {
      @include text_body1_medium;
      color: var(--color-black-1);
      word-wrap: anywhere;
    }

    &__description {
      @include text_body2;
      color: var(--color-gray-text);
    }

    &__sign {
      flex-shrink: 0;
      width: 100px;
      min-height: 100px;
      border-left: 1px solid var(--color-gray-line);
      background: url('~/shared/icons/i-sign.svg') no-repeat center;
      background-size: 48px;
    }
  }
}

@media (max-width: 479px) {
  .item {
    &_hasPhoto {
      .item__info {
        display: grid;
        grid-template-areas: 'photo inner' 'sign inner';

        &__photoWrapper {
          grid-area: photo;
          width: 80px;
          padding: var(--space-2-5x) 10px;
        }
        &__inner {
          grid-area: inner;
        }
        &__sign {
          width: 80px;
          padding: var(--space-2-5x) 10px;
          grid-area: sign;
          border-right: 1px solid var(--color-gray-line);
          border-top: 1px solid var(--color-gray-line);
          border-left: none;
          display: flex;
          justify-content: center;
        }
      }
    }
  }
}
</style>
